import { array, nonEmptyArray, option, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';
import { getCategoryServiceId } from '../../selectors/views/categories';
import { ApplicationState } from '../../types';
import { WithCategoryId } from '../../types/selectors/selectorPropTypes';
import { getEnterpriseDatesOccupancy } from '../../views/dates/slices/selectors';
import { getAgeCategories, getUniqueAgeCategoriesPerClassification } from '../entities/ageCategories/selectors';
import { getViewResourceOrders } from '../entities/reservations/selectors';
import {
    getCategoryEnterpriseBaseOccupancies,
    getCurrentDateCategoryEnterpriseMaximumAvailabilityCount,
} from '../occupancy/selectors';
import { getCategoryMaxPersonCount } from '../resourceCategories/selectors';
import { distributeRooms } from './distributeRooms';
import { OccupancyData } from './occupancyTypes';

// Sum persons from summary by age categories
const getViewResourceOrdersAgeCategoriesCount = (state: ApplicationState): Record<string, OccupancyData> =>
    pipe(
        getViewResourceOrders(state),
        array.map((resourceOrder) =>
            pipe(
                resourceOrder.reservation.occupancyData ?? [],
                array.map((occupancyData) => ({
                    ...occupancyData,
                    personCount: occupancyData.personCount * resourceOrder.reservation.count,
                }))
            )
        ),
        array.flatten,
        nonEmptyArray.groupBy((occupancyData) => occupancyData.ageCategoryId),
        record.toArray,
        array.reduce({}, (accumulator, [ageCategoryId, occupancyData]) => {
            const occupancySum = pipe(
                occupancyData,
                array.reduce({ ageCategoryId, personCount: 0 }, (occupancyAccumulator, occupancyDataItem) => ({
                    ...occupancyAccumulator,
                    personCount: occupancyAccumulator.personCount + occupancyDataItem.personCount,
                }))
            );

            return {
                ...accumulator,
                [ageCategoryId]: occupancySum,
            };
        })
    );

const getCategoryDefaultAgeCategory = createSelector(
    getAgeCategories,
    getCategoryServiceId,
    (ageCategories, serviceId) =>
        pipe(
            ageCategories,
            array.findFirst((ageCategory) => ageCategory.isDefault && ageCategory.serviceId === serviceId)
        )
);

const getAdultAndChildAgeCategories = (state: ApplicationState, { categoryId }: WithCategoryId) => {
    const serviceId = getCategoryServiceId(state, { categoryId });

    if (serviceId == null) {
        return { adultAgeCategoryId: null, childAgeCategoryId: null };
    }

    return getUniqueAgeCategoriesPerClassification(state, { serviceId });
};

// Get initial room occupancies from initial occupancy (on dates screen)
export const getInitialRoomOccupanciesFromDateOccupancy = createSelector(
    getCategoryMaxPersonCount,
    getEnterpriseDatesOccupancy,
    getCategoryEnterpriseBaseOccupancies,
    getCurrentDateCategoryEnterpriseMaximumAvailabilityCount,
    getViewResourceOrdersAgeCategoriesCount,
    getCategoryDefaultAgeCategory,
    getAdultAndChildAgeCategories,
    (
        maxPersonCount,
        datesOccupancy,
        baseOccupancies,
        maximumAvailabilityCount,
        summaryOccupancy,
        defaultAgeCategory,
        adultAndChildCategories
    ) => {
        const preselectedOccupancy = Object.values(datesOccupancy);

        if (preselectedOccupancy.length === 0) {
            if (adultAndChildCategories.adultAgeCategoryId !== null && baseOccupancies?.adultCount != null) {
                preselectedOccupancy.push({
                    ageCategoryId: adultAndChildCategories.adultAgeCategoryId,
                    personCount: baseOccupancies.adultCount,
                });
            }

            if (adultAndChildCategories.childAgeCategoryId !== null && baseOccupancies?.childCount != null) {
                preselectedOccupancy.push({
                    ageCategoryId: adultAndChildCategories.childAgeCategoryId,
                    personCount: baseOccupancies.childCount,
                });
            }
        }

        // Subtract persons already added in summary
        const processedOccupancyData: OccupancyData[] = pipe(
            preselectedOccupancy,
            array.map((datesOccupancyItem) => ({
                ...datesOccupancyItem,
                personCount: Math.max(
                    0,
                    datesOccupancyItem.personCount -
                        (summaryOccupancy[datesOccupancyItem.ageCategoryId]?.personCount ?? 0)
                ),
            }))
        );

        const personSum = pipe(
            processedOccupancyData,
            array.reduce(0, (accumulator, occupancyData) => accumulator + occupancyData.personCount)
        );

        // Fallback when there is nobody
        if (personSum === 0 || processedOccupancyData.length === 0) {
            if (option.isSome(defaultAgeCategory)) {
                processedOccupancyData.push({ ageCategoryId: defaultAgeCategory.value.id, personCount: 1 });
            }
        }

        return distributeRooms({
            maxPersonCount,
            maxRoomCount: maximumAvailabilityCount,
            datesOccupancy: processedOccupancyData,
        });
    }
);
